import { Chip } from '@mui/material';

import FeatureLink from '@/components/FeatureLink/FeatureLink';
import Icon, { IconName } from '@/components/Icon/Icon';
import Link from '@/components/Link/Link';
import {
  ContentStyle,
  type FieldlessDropdownMenu,
  type FieldlessDropdownMenuItem,
} from '@/contentful';
import useViewPortSize, {
  type UseViewportSizeType,
} from '@/hooks/useViewportSize';
import RightArrow from '@/public/cmsAssets/assets/img/RightArrow';
import { brandColors } from '@/styles/theme/values/brandColors';
import { ViewportSize } from '@/styles/viewportSize';
import { trackEvents } from '@/utils/analytics';
import { getFrontifyBackgroundImageUrl } from '@/utils/frontify';

import useStyles from './Dropdown.styles';

export interface DropdownProps {
  dropdown: FieldlessDropdownMenu;
  isOnDark: boolean;
  closeMobileDropdown?: () => void;
  isHelperNav?: boolean;
}

const getImageSize = (viewports: UseViewportSizeType) => {
  const { isMedium, isExtraSmall, isSmall } = viewports;

  if (isExtraSmall) return ViewportSize.sm;
  if (isSmall) return ViewportSize.md;
  if (isMedium) return ViewportSize.lg;

  return ViewportSize.lg;
};

const Dropdown = ({
  dropdown,
  closeMobileDropdown,
  isOnDark,
  isHelperNav = false,
}: DropdownProps) => {
  const viewports = useViewPortSize();
  const { classes, cx } = useStyles();
  const links: FieldlessDropdownMenuItem[] = dropdown.links;
  const { cardTitle, image, buttonLabel, link } = dropdown?.featured ?? {};
  const featureLinkIsOnDark = dropdown?.featured?.style === ContentStyle.OnDark;
  const shouldShowDark = isOnDark && !isHelperNav;

  const dropDownItemClass = cx({
    [classes.dropdownItem]: true,
    [classes.dropdownItemDark]: shouldShowDark,
  });

  const backArrowClass = cx({
    [classes.backArrowText]: true,
    [classes.backArrowTextDark]: shouldShowDark,
  });

  const dropDownTitleClass = cx({
    [classes.dropdownTitle]: true,
    [classes.dropdownTitleDark]: shouldShowDark,
  });

  const containerClass = cx({
    [classes.dropdown]: true,
    [classes.dropdownDark]: shouldShowDark,
  });

  return (
    <div className={containerClass}>
      <div className={classes.innerWrapper}>
        <ul className={classes.dropdownList}>
          {/** biome-ignore lint/a11y/useSemanticElements: Automated migration to Biome */}
          <div
            className={classes.backArrowContainer}
            role='button'
            tabIndex={0} // Focusable
            aria-label='Go back'
            onClick={() => {
              if (closeMobileDropdown) {
                closeMobileDropdown();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                if (closeMobileDropdown) {
                  closeMobileDropdown();
                }
              }
            }}
          >
            <Icon
              name={IconName.CHEVRON_LEFT}
              width={20}
              color={
                shouldShowDark ? brandColors.chargeBlue : brandColors.mediumBlue
              }
            />
            <div className={backArrowClass}>All</div>
          </div>
          <div className={dropDownTitleClass}>Explore</div>
          {links.map(
            (
              {
                pageTitle,
                pageLink,
                chipText,
                subtext,
              }: FieldlessDropdownMenuItem,
              i: number
            ) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                key={i}
                className={dropDownItemClass}
                data-testid={`dropdownItem-${i}`}
              >
                <Link
                  href={pageLink as string}
                  className={classes.link}
                  noPrefetchOnHover
                >
                  {/** biome-ignore lint/a11y/useSemanticElements: Automated migration to Biome */}
                  <div
                    role='button'
                    tabIndex={-1}
                    aria-label={`Navigate to ${pageTitle}`}
                    onClick={() =>
                      trackEvents([
                        {
                          event: 'engagement',
                          id: `nav-dropdown-item-${pageTitle}`,
                        },
                      ])
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        trackEvents([
                          {
                            event: 'engagement',
                            id: `nav-dropdown-item-${pageTitle}`,
                          },
                        ]);
                      }
                    }}
                  >
                    <div
                      className={cx(
                        classes.pageTitle,
                        shouldShowDark && classes.pageTitleDark
                      )}
                    >
                      <span>
                        {pageTitle as string}
                        {chipText && (
                          <Chip
                            className={cx(
                              classes.chip,
                              shouldShowDark && classes.chipDark
                            )}
                            label={chipText as string}
                          />
                        )}
                        {subtext && (
                          <div
                            className={cx(
                              classes.linkSubtext,
                              shouldShowDark && classes.linkSubtextDark
                            )}
                          >
                            {subtext}
                          </div>
                        )}
                      </span>
                      <RightArrow
                        fill={
                          shouldShowDark
                            ? brandColors.white
                            : brandColors.primaryMain
                        }
                      />
                    </div>
                  </div>
                </Link>
              </li>
            )
          )}
        </ul>
        <div className={classes.featured}>
          <div className={dropDownTitleClass}>Featured</div>
          <div className={classes.featureCardWrapper}>
            <FeatureLink
              category={dropdown.name}
              title={cardTitle}
              imageUrl={getFrontifyBackgroundImageUrl(
                image[0].preview_url,
                getImageSize(viewports)
              )}
              buttonLabel={buttonLabel}
              url={link}
              isOnDark={featureLinkIsOnDark}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dropdown;
